import dayjs, { Dayjs } from 'dayjs';
import { FormMessage } from '../../types';
import { assignEnrollmentSections } from '../../helpers/enrollmentHelpers';

export const parseDelayToMinutes = (delay?: string | null): number | null => {
  if (!delay) return null;
  const m = /^(-?\d+)([mhd])$/i.exec(delay.trim());
  if (!m) return null;
  const n = parseInt(m[1], 10);
  if (Number.isNaN(n)) return null;
  const unit = m[2].toLowerCase();
  return unit === 'm' ? n : unit === 'h' ? n * 60 : n * 1440;
};

const parseDate = (value?: string | null): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

export interface MessageOrderResult {
  valid: boolean;
  /** Message position → why that message is out of order. */
  errorsByPosition: Record<number, string>;
  /** Message position → earliest time its picker may offer, ISO. */
  minTimeByPosition: Record<number, string | null>;
}

/**
 * Enrollment follow-ups get their own two messages, kept separate from the
 * generic order validator: after a submit these more specific messages replace
 * its generic wording.
 */
export const validateEnrollmentFollowUpTimes = (
  messages: FormMessage[],
  joinEndAt?: string | null,
): Record<number, string> => {
  const errors: Record<number, string> = {};
  const joinEnd = parseDate(joinEndAt);
  const sectionedMessages = messages.some(message => message.enrollmentSection)
    ? messages
    : assignEnrollmentSections(messages);

  sectionedMessages.forEach(message => {
    if (message.enrollmentSection !== 'scheduled' || message.scheduledType !== 'time') return;

    const messageTime = parseDate(message.time);
    if (!messageTime) {
      errors[message.position] = 'Follow up message time is required.';
      return;
    }

    if (joinEnd && !messageTime.isAfter(joinEnd)) {
      errors[message.position] =
        'Follow up message time must be later than the join window end time.';
    }
  });

  return errors;
};

/**
 * Each message must go out after the one before it.
 *
 * The list is walked by position, carrying the running send time forward. A
 * delay is measured from THAT time, not from the campaign start: two messages
 * both set to "30 minutes later" land 30 and 60 minutes out, and are in order.
 *
 * Some messages have no place on the clock. 'immediate' rides along with
 * whatever preceded it, so it neither breaks the sequence nor moves it. A reply
 * or a calendar trigger could arrive at any time, so they unanchor the sequence
 * and nothing after them can be judged — including a delay, which then has
 * nothing to measure from.
 */
export const validateMessageOrder = (
  messages: FormMessage[],
  startAt?: string | null,
): MessageOrderResult => {
  const ordered = [...messages].sort((a, b) => a.position - b.position);
  const errorsByPosition: Record<number, string> = {};
  const minTimeByPosition: Record<number, string | null> = {};

  let previous = parseDate(startAt);

  ordered.forEach((message) => {
    minTimeByPosition[message.position] = previous
      ? previous.add(1, 'minute').toISOString()
      : null;

    if (message.scheduledType === 'time') {
      const sendAt = parseDate(message.time);
      if (sendAt && previous && !sendAt.isAfter(previous)) {
        errorsByPosition[message.position] =
          'Scheduled message time must be after the previous message time.';
      }
      previous = sendAt;
      return;
    }

    if (message.scheduledType === 'delay') {
      const delayMinutes = parseDelayToMinutes(message.delay);
      if (previous && delayMinutes !== null) {
        const sendAt = previous.add(delayMinutes, 'minute');
        if (!sendAt.isAfter(previous)) {
          errorsByPosition[message.position] =
            'Delay must schedule this message after the previous message time.';
        }
        previous = sendAt;
        return;
      }
      previous = null;
      return;
    }

    if (message.scheduledType === 'immediate') return;

    previous = null;
  });

  return {
    valid: Object.keys(errorsByPosition).length === 0,
    errorsByPosition,
    minTimeByPosition,
  };
};
